import { useMemo, useState } from "preact/hooks"
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { row_tooltip, DataTable, Figure, GRID_COLOR, InlineBar, KEHE_OPS_COLOR, Legend, MUTED_COLOR, Panel, RETAILER_NW_COLOR, Tile, WORKING_COLOR, column } from "./trade_review_parts"
import {
  PLACEHOLDER_COGS_PCT,
  contribution,
  dc_cost_row,
  fine_month,
  fmt_int,
  fmt_month,
  fmt_pct0,
  fmt_pct1,
  fmt_usd2,
  fmt_usd_short,
  highest_cost_first,
  kehe_cost_row,
  oi_row,
  pnl_row,
  trade_review,
} from "./trade_review_model"

type stack_row = pnl_row & { working_rate: number; retail_nw_rate: number; terms_rate: number; ops_rate: number }

function stack_rows(rows: pnl_row[]): stack_row[] {
  return highest_cost_first(rows).map((row: pnl_row) => ({
    ...row,
    working_rate: row.working / row.sales,
    retail_nw_rate: row.retail_nw / row.sales,
    terms_rate: row.terms / row.sales,
    ops_rate: row.ops_prog / row.sales,
  }))
}

function CostToServeChart({ rows }: { rows: pnl_row[] }) {
  const data = useMemo(() => stack_rows(rows), [rows])
  return (
    <Panel
      title="What each retailer costs to serve, share of sales"
      note="Working trade and retailer non-working costs are charged to the retailer that earned them. KeHE costs are amortized across retailers: terms by sales, and operations through the DC that serves each retailer. Highest total first.">
      <div style={{ height: `${data.length * 28 + 32}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 48, bottom: 0, left: 0 }} barCategoryGap={5}>
            <CartesianGrid horizontal={false} stroke={GRID_COLOR} />
            <XAxis type="number" domain={[0, 0.35]} ticks={[0, 0.1, 0.2, 0.3]} tickFormatter={fmt_pct0} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="retailer" width={170} interval={0} tick={{ fontSize: 12 }} />
            <Tooltip
              cursor={{ fill: "transparent" }}
              content={row_tooltip((row: stack_row) => [
                      row.retailer,
                      `Working ${fmt_pct1(row.working_rate)}`,
                      `Retailer non-working ${fmt_pct1(row.retail_nw_rate)}`,
                      `Fees ${fmt_usd_short(row.fees)}, placement ${fmt_usd_short(row.placement)}, fair share ${fmt_usd_short(row.fair_share)}`,
                      `KeHE terms ${fmt_pct1(row.terms_rate)}`,
                      `KeHE operations ${fmt_pct1(row.ops_rate)}`,
                    ]) as never}
            />
            <Bar dataKey="working_rate" stackId="cost" fill={WORKING_COLOR} isAnimationActive={false} />
            <Bar dataKey="retail_nw_rate" stackId="cost" fill={RETAILER_NW_COLOR} isAnimationActive={false} />
            <Bar dataKey="terms_rate" stackId="cost" fill={MUTED_COLOR} isAnimationActive={false} />
            <Bar dataKey="ops_rate" stackId="cost" fill={KEHE_OPS_COLOR} isAnimationActive={false}>
              <LabelList dataKey="total_pct" position="right" formatter={fmt_pct1} fontSize={12} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Legend
        items={[
          { label: "Working trade", color: WORKING_COLOR },
          { label: "Retailer non-working", color: RETAILER_NW_COLOR },
          { label: "KeHE terms", color: MUTED_COLOR },
          { label: "KeHE operations and programs", color: KEHE_OPS_COLOR },
        ]}
      />
    </Panel>
  )
}

function pnl_columns(cogs_pct: number): column<pnl_row>[] {
  return [
    { header: "Retailer", left: true, cell: (row: pnl_row) => row.retailer },
    { header: "Sales", cell: (row: pnl_row) => fmt_usd_short(row.sales) },
    { header: "Working trade", cell: (row: pnl_row) => fmt_usd_short(row.working) },
    { header: "Retailer non-working", cell: (row: pnl_row) => fmt_usd_short(row.retail_nw) },
    { header: "KeHE terms", cell: (row: pnl_row) => fmt_usd_short(row.terms) },
    { header: "KeHE operations", cell: (row: pnl_row) => fmt_usd_short(row.ops_prog) },
    { header: "Net after all costs", cell: (row: pnl_row) => fmt_usd_short(row.net) },
    { header: "Total cost rate", cell: (row: pnl_row) => fmt_pct1(row.total_pct) },
    { header: "Working share", cell: (row: pnl_row) => fmt_pct0(row.working_share) },
    { header: "Net per unit", cell: (row: pnl_row) => fmt_usd2(row.net_unit) },
    { header: "Contribution", cell: (row: pnl_row) => fmt_usd_short(contribution(row.net, row.sales, cogs_pct)) },
  ]
}

const COST_COLUMNS: column<kehe_cost_row>[] = [
  { header: "Cost", left: true, wrap: true, cell: (row: kehe_cost_row) => row.cost },
  { header: "Amount", cell: (row: kehe_cost_row) => fmt_usd_short(row.amount) },
  { header: "Share of sales", cell: (row: kehe_cost_row) => fmt_pct1(row.pct_sales) },
  { header: "Spread by", left: true, cell: (row: kehe_cost_row) => row.driver },
]

const FINE_COLUMNS = (top: number): column<fine_month>[] => [
  { header: "Month", left: true, cell: (row: fine_month) => fmt_month(row.month) },
  { header: "Charges", cell: (row: fine_month) => fmt_int(row.charges) },
  { header: "Amount", left: true, cell: (row: fine_month) => <InlineBar share={row.amount / top} label={fmt_usd_short(row.amount)} /> },
]

const DC_COLUMNS = (top: number): column<dc_cost_row>[] => [
  { header: "DC", left: true, cell: (row: dc_cost_row) => row.label },
  { header: "Sales", cell: (row: dc_cost_row) => fmt_usd_short(row.sales) },
  { header: "Shorts", cell: (row: dc_cost_row) => fmt_usd_short(row.shorts) },
  { header: "Fines", cell: (row: dc_cost_row) => fmt_usd_short(row.fines) },
  { header: "Fuel", cell: (row: dc_cost_row) => fmt_usd_short(row.fuel) },
  { header: "Credits", cell: (row: dc_cost_row) => fmt_usd_short(row.credits) },
  { header: "Share of sales", left: true, cell: (row: dc_cost_row) => <InlineBar share={row.ops_rate / top} label={fmt_pct1(row.ops_rate)} /> },
]

const OI_COLUMNS: column<oi_row>[] = [
  { header: "Where we looked", left: true, cell: (row: oi_row) => row.source },
  { header: "What we checked", left: true, wrap: true, cell: (row: oi_row) => row.checked },
  { header: "What we found", left: true, wrap: true, cell: (row: oi_row) => row.found },
]

export function ProfitTab({ data }: { data: trade_review }) {
  const [cogs_pct, set_cogs_pct] = useState(PLACEHOLDER_COGS_PCT)
  const s = data.summary
  const top_fine = Math.max(...data.fines_810.map((row: fine_month) => row.amount))
  const top_dc = Math.max(...data.dc_costs.map((row: dc_cost_row) => row.ops_rate))

  function change_cogs(event: Event) {
    const value = Number((event.target as HTMLInputElement).value)
    if (value >= 0 && value <= 100) set_cogs_pct(value)
  }

  return (
    <div class="space-y-6">
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile label="Working trade" value={fmt_usd_short(s["working"] ?? 0)} note={<><Figure summary={s} field="working_pct" as="pct1" /> of sales. Scan, TPR, MCB and retail ad money that reaches the shopper</>} />
        <Tile label="Retailer non-working" value={fmt_usd_short(s["retail_nw"] ?? 0)} note="Processing fees, free fills, slotting, fair share and returns" />
        <Tile label="KeHE terms" value={fmt_usd_short(s["kehe_terms"] ?? 0)} note={<>Laura Chenel <Figure summary={s} field="kehe_terms_lc" as="usd_short" />, Marin French <Figure summary={s} field="kehe_terms_mfc" as="usd_short" />. Each company's KeHE terms form sets spoilage, CONNECT BI and prompt pay at 2% of what it invoices KeHE</>} />
        <Tile label="KeHE operations" value={fmt_usd_short(s["kehe_ops"] ?? 0)} note="Shorts, fines and fuel, net of credits, charged by DC" />
      </div>

      <CostToServeChart rows={data.retailer_pnl} />

      <Panel title="Retailer P&L through KeHE" note="We don't have standard costs yet, so contribution uses one placeholder cost rate for every item. Replace it with real numbers from JD Edwards. Broker commissions are not included.">
        <div class="flex items-center gap-2">
          <Label htmlFor="trade-review-cogs">Cost of goods, % of sales (placeholder)</Label>
          <Input id="trade-review-cogs" type="number" min={0} max={100} className="w-20" value={cogs_pct} onChange={change_cogs} />
        </div>
        <DataTable columns={pnl_columns(cogs_pct)} rows={data.retailer_pnl} row_key={(row: pnl_row) => row.retailer} />
        <p class="text-sm text-muted-foreground">
          <Figure summary={s} field="fy25_amt" as="usd_short" /> billed after December 1 for promos that ran in 2025 is left out, so this view shows 2026 activity only. Promos from the last few weeks may still be billed.
        </p>
      </Panel>

      <div class="grid gap-4 xl:grid-cols-2">
        <Panel title="What KeHE itself costs" note={<>January through October. The two 2% allowances total <Figure summary={s} field="spo_bi_pct" as="pct1" /> of what you invoiced KeHE, which is the 4% rebate on your calendar.</>}>
          <DataTable columns={COST_COLUMNS} rows={data.kehe_costs} row_key={(row: kehe_cost_row) => row.cost} />
        </Panel>
        <Panel title="KeHE operating cost by DC" note={<>Shorts, fines and fuel less credits, as a share of each DC's sales. KeHE's sales report has no DC, so we assigned each retailer to the DC that serves it. Those sales come to <Figure summary={s} field="dc_fit" as="pct0" /> of what you invoiced KeHE.</>}>
          <DataTable columns={DC_COLUMNS(top_dc)} rows={data.dc_costs} row_key={(row: dc_cost_row) => row.label} />
        </Panel>
      </div>

      <Panel title="810 invoice fines by month" note="KeHE bills these per non-compliant invoice. They began in July.">
        <DataTable columns={FINE_COLUMNS(top_fine)} rows={data.fines_810} row_key={(row: fine_month) => row.month} />
      </Panel>

      <Panel title="Off-invoice: KeHE terms are included, Costco is not yet" note="KeHE's two 2% allowances are off-invoice by contract and already appear above as KeHE terms. We found no off-invoice promos at KeHE. Costco's discounts come straight off the invoice in JD Edwards, so no deduction reaches us.">
        <DataTable columns={OI_COLUMNS} rows={data.oi_status} row_key={(row: oi_row) => row.source} />
        <div class="space-y-2 text-sm max-w-3xl">
          <h3 class="font-medium">How we would add it</h3>
          <p>Each off-invoice deal goes on its promotion as an Off Invoice line with its own buy dates, next to any scan or billback on the same event. MarginWiz already holds that line type, so a retailer can carry either method.</p>
          <p>Spend then comes from JD Edwards invoice lines showing gross price, both discounts and net. With no deduction to match, actual spend is the discount on each invoice, and it lands in this P&amp;L as working or non-working based on what the money funds. Costco's demo discount would be working.</p>
          <p>Lift needs Costco's own sales, because orders alone can show a buy-in ahead of a deal instead of real demand.</p>
        </div>
      </Panel>
    </div>
  )
}
